// BL-39/40/41 — one staff member: who they are, what they are doing now, their
// totals (admin/superadmin only) and their bookings. Replaces BL-22's smaller
// roster popup.
//
// The layout follows the AgriChain profile the intake names as the reference —
// its STRUCTURE (profile card left; totals and history right), built from
// packages/ui and our tokens. A modal rather than a page (Leon, 2026-10-04), so
// the roster stays behind it and the same component can open over the Live Map
// later (BL-36) without leaving it.
//
// Built role-aware from the start (intake 2026-09-26): what a viewer sees and
// may change comes from `staffDetailAccess`, never from a check in the markup.

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bednbody/ui/components/tabs";
import { cn } from "@bednbody/ui/lib/utils";
import { useEffect, useRef, useState } from "react";
import type { StaffRole } from "@/lib/jwt-claims";
import { StatusPill } from "../../live-map/LiveMapCards";
import { useLiveMapContext } from "../../live-map/live-map.hooks";
import { GROUP_TONE, staffStatusView } from "../../live-map/live-map.status";
import type { RiderVehicle } from "../../staff-media/staff-media.hooks";
import { roleLabel, StaffEditDialogs, type StaffEditKind } from "../components/StaffEditDialogs";
import type { RosterPerson } from "../components/types";
import { StaffBookingHistory } from "./StaffBookingHistory";
import { StaffPayoutHistory } from "./StaffPayoutHistory";
import { StaffServiceSwitches } from "./StaffServiceSwitches";
import { StaffProfileCard } from "./StaffProfileCard";
import { StaffTotals } from "./StaffTotals";
import { hasBookingHistory, staffDetailAccess } from "./staff-detail.access";

const sentenceCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" });

/**
 * What they are doing right now, from the same status engine the Live Map and
 * the dashboard use. Mounted only while the modal is open, so the roster page
 * itself never polls the staff-location feed.
 */
function LiveStatus({ person }: { person: RosterPerson }) {
  const { data, isError } = useLiveMapContext();
  if (isError) return <StatusPill label="Status unavailable" tone="muted" />;
  if (!data) return null;
  const live = data.clockedInStaff.find((member) => member.id === person.id);
  if (!live) return <StatusPill label="Not in session" tone="muted" />;
  // `false`: only the pre-engine fallback reads dispatch, and engine statuses
  // carry their own — the dashboard makes the same call.
  const status = staffStatusView(live, false);
  return (
    <span className="flex items-center gap-2">
      <StatusPill label={status.label} tone={GROUP_TONE[status.group]} />
      {status.since ? (
        <span className="text-xs text-muted-foreground">since {timeLabel(status.since)}</span>
      ) : null}
    </span>
  );
}

interface StaffDetailModalProps {
  person: RosterPerson | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewerRole: StaffRole | null;
  photo: string | null;
  vehicle: RiderVehicle | null;
}

export function StaffDetailModal({
  person,
  open,
  onOpenChange,
  viewerRole,
  photo,
  vehicle,
}: StaffDetailModalProps) {
  // The last person shown, so the modal does not empty while it fades out.
  const last = useRef<RosterPerson | null>(person);
  useEffect(() => {
    if (person) last.current = person;
  }, [person]);
  const shown = person ?? last.current;

  const [dialog, setDialog] = useState<StaffEditKind | null>(null);
  const [tab, setTab] = useState("bookings");
  // A different person always opens on their bookings.
  const [tabFor, setTabFor] = useState(shown?.id ?? null);
  if ((shown?.id ?? null) !== tabFor) {
    setTabFor(shown?.id ?? null);
    setTab("bookings");
  }

  const access = shown ? staffDetailAccess(viewerRole, shown) : null;
  // Office staff have no bookings and no totals, so they get the profile alone
  // in a narrow modal instead of a wide one with an empty right half.
  const withHistory = shown ? hasBookingHistory(shown.role) : false;

  return (
    <>
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent
          className={cn(
            // Squarer and calmer than the client apps' dialogs: this is the
            // admin console, and the cards inside carry the structure.
            "flex max-h-[90vh] flex-col gap-0 overflow-hidden rounded-2xl bg-background p-0",
            withHistory ? "sm:max-w-6xl" : "sm:max-w-md",
          )}
        >
          {shown && access ? (
            <>
              <DialogHeader className="gap-1 border-b border-border bg-card px-6 py-5 pr-14">
                <DialogTitle className="text-2xl font-semibold tracking-tight text-foreground">
                  {shown.name}
                </DialogTitle>
                <DialogDescription>
                  {sentenceCase(roleLabel(shown.role))} profile · {shown.hubName ?? "No hub"}
                </DialogDescription>
              </DialogHeader>

              <div className="min-h-0 flex-1 overflow-y-auto p-6">
                <div
                  className={cn(
                    "grid gap-6",
                    withHistory && "lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start",
                  )}
                >
                  <div className="min-w-0 space-y-6">
                    <StaffProfileCard
                      access={access}
                      liveStatus={withHistory ? <LiveStatus person={shown} /> : null}
                      onEdit={setDialog}
                      person={shown}
                      photo={photo}
                      vehicle={vehicle}
                    />
                    {access.canSeeServices ? (
                      <StaffServiceSwitches
                        canEdit={access.canSetServices}
                        therapistId={shown.id}
                      />
                    ) : null}
                  </div>
                  {withHistory ? (
                    <div className="min-w-0 space-y-6">
                      {access.canSeeMoney ? <StaffTotals person={shown} /> : null}
                      {/* Payouts are admin/superadmin only — `payout_requests`
                          carries no grant for anyone else, so a hub manager is
                          not offered a tab the database would refuse. */}
                      {access.canSeeMoney ? (
                        <Tabs onValueChange={(value) => setTab(String(value))} value={tab}>
                          <TabsList
                            className="w-full justify-start border-b border-border"
                            variant="line"
                          >
                            <TabsTrigger className="flex-none px-3" value="bookings">
                              Bookings
                            </TabsTrigger>
                            <TabsTrigger className="flex-none px-3" value="payouts">
                              Payouts
                            </TabsTrigger>
                          </TabsList>
                          <TabsContent value="bookings">
                            <StaffBookingHistory canSeeMoney person={shown} />
                          </TabsContent>
                          <TabsContent value="payouts">
                            <StaffPayoutHistory person={shown} />
                          </TabsContent>
                        </Tabs>
                      ) : (
                        <StaffBookingHistory canSeeMoney={false} person={shown} />
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {shown ? (
        <StaffEditDialogs
          dialog={dialog}
          onDialogChange={setDialog}
          person={shown}
          vehicle={vehicle}
        />
      ) : null}
    </>
  );
}
